import React from "react";
import { motion } from "framer-motion";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const pointVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
    },
  },
};

const RoutePricingDiagram = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: 450,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.03
        )}, ${alpha(theme.palette.success.main, 0.03)})`,
        borderRadius: 3,
        p: 3,
        overflow: "hidden",
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Central Point - Restaurant */}
        <motion.div variants={pointVariants}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <StorefrontIcon
              sx={{
                fontSize: 48,
                color: "primary.main",
                filter: `drop-shadow(0 2px 8px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )})`,
              }}
            />
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              🏪 Restaurante (Ponto de Partida)
            </Typography>
          </Box>
        </motion.div>

        {/* Route visualization with vertical flow */}
        <Stack spacing={2}>
          {/* Cliente C - Closest */}
          <motion.div variants={pointVariants}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderLeft: `4px solid ${theme.palette.success.main}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocationOnIcon color="success" />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Cliente C (2km)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mais próximo na rota
                  </Typography>
                </Box>
                <Chip
                  label="R$ Preço rota"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: "success.main",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Paper>
          </motion.div>

          {/* Arrow */}
          <Box sx={{ pl: 3 }}>
            <ArrowDownwardIcon sx={{ color: "text.secondary" }} />
          </Box>

          {/* Cliente B - Middle */}
          <motion.div variants={pointVariants}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderLeft: `4px solid ${theme.palette.success.main}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocationOnIcon color="success" />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Cliente B (5km)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No meio da rota
                  </Typography>
                </Box>
                <Chip
                  label="R$ Preço rota"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: "success.main",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Paper>
          </motion.div>

          {/* Arrow */}
          <Box sx={{ pl: 3 }}>
            <ArrowDownwardIcon sx={{ color: "text.secondary" }} />
          </Box>

          {/* Cliente A - Furthest */}
          <motion.div variants={pointVariants}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderLeft: `4px solid ${theme.palette.warning.main}`,
                background: alpha(theme.palette.warning.main, 0.05),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocationOnIcon sx={{ color: "warning.main" }} />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Cliente A (9km) ⭐
                  </Typography>
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight={600}
                  >
                    Mais distante - Taxa Integral
                  </Typography>
                </Box>
                <Chip
                  label="R$ Preço integral"
                  size="small"
                  sx={{
                    bgcolor: theme.palette.warning.main,
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Paper>
          </motion.div>
        </Stack>

        {/* Explanation */}
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.info.main, 0.1),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <Typography variant="body2" color="info.main" fontWeight={500}>
            💡 O entregador recebe o valor total apenas do pedido mais distante.
            Os outros pedidos na mesma rota pagam o valor configurado por você.
          </Typography>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default RoutePricingDiagram;
